import { useState, useCallback, useMemo } from 'react';
import { usePaymentQuery } from './usePaymentQuery';
import { getBasisTheoryApiKey } from '../../../react/config/payment';
export function useApplePayCheckout({ checkout, onSuccess, onError, onCancel, }) {
    const [processingPayment, setProcessingPayment] = useState(false);
    const [error, setError] = useState(null);
    const { processApplePayPayment } = usePaymentQuery();
    const basistheoryPublicKey = useMemo(() => getBasisTheoryApiKey(), []);
    // Validate merchant with Basis Theory
    const validateMerchant = useCallback(async () => {
        try {
            const response = await fetch('https://api.basistheory.com/apple-pay/session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': basistheoryPublicKey,
                },
                body: JSON.stringify({
                    display_name: checkout?.checkoutSession?.store?.name || 'Store',
                    domain: window.location.host,
                }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return await response.json();
        }
        catch (err) {
            console.error('Merchant validation failed:', err);
            throw err;
        }
    }, [basistheoryPublicKey, checkout?.checkoutSession?.store?.name]);
    // Tokenize Apple Pay payment
    const tokenizeApplePay = useCallback(async (event) => {
        try {
            const response = await fetch('https://api.basistheory.com/apple-pay', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': basistheoryPublicKey,
                },
                body: JSON.stringify({
                    apple_payment_data: event.payment.token,
                }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const result = await response.json();
            return result.apple_pay;
        }
        catch (err) {
            console.error('Tokenization failed:', err);
            throw err;
        }
    }, [basistheoryPublicKey]);
    // Handle Apple Pay payment click
    const handleApplePayClick = useCallback(() => {
        // Don't proceed if checkout is not available
        if (!checkout) {
            console.error('Checkout data not available');
            if (onError) {
                onError('Checkout not ready');
            }
            return;
        }
        // Create line items from checkout summary
        const lineItems = [
            {
                label: 'Subtotal',
                amount: (checkout.summary.subtotalAdjustedAmount / 100).toFixed(2),
                type: 'final',
            },
            {
                label: 'Shipping',
                amount: ((checkout.summary.shippingCost ?? 0) / 100).toFixed(2),
                type: 'final',
            },
            {
                label: 'Tax',
                amount: (checkout.summary.totalTaxAmount / 100).toFixed(2),
                type: 'final',
            },
        ];
        const total = {
            label: checkout.checkoutSession.store?.name || 'Store',
            amount: (checkout.summary.totalAdjustedAmount / 100).toFixed(2),
            type: 'final',
        };
        const request = {
            countryCode: 'US', // Could be from payment method metadata
            currencyCode: checkout.summary.currency,
            supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
            merchantCapabilities: ['supports3DS'],
            total,
            lineItems,
        };
        try {
            const session = new window.ApplePaySession(3, request);
            // Merchant validation
            session.onvalidatemerchant = (event) => {
                void (async () => {
                    try {
                        const merchantSession = await validateMerchant();
                        session.completeMerchantValidation(merchantSession);
                    }
                    catch (error) {
                        console.error('Merchant validation failed:', error);
                        session.abort();
                        if (onError) {
                            onError('Merchant validation failed');
                        }
                    }
                })();
            };
            // Payment authorized
            session.onpaymentauthorized = (event) => {
                void (async () => {
                    try {
                        setProcessingPayment(true);
                        // Tokenize payment
                        const applePayToken = await tokenizeApplePay(event);
                        // Complete Apple Pay sheet immediately after tokenization
                        // Apple Pay requires completePayment within ~30s of authorization
                        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                        // Process payment via SDK hook
                        // onSuccess/redirect is gated through onPaymentSuccess callback only,
                        // so we never redirect until the backend confirms success
                        await processApplePayPayment(checkout.checkoutSession.id, applePayToken, {
                            onPaymentSuccess: (response) => {
                                if (onSuccess) {
                                    onSuccess(response);
                                }
                            },
                            onPaymentFailed: (err) => {
                                setProcessingPayment(false);
                                setError(err.message);
                                if (onError) {
                                    onError(err.message);
                                }
                            },
                        });
                    }
                    catch (error) {
                        console.error('Payment failed:', error);
                        setProcessingPayment(false);
                        const errorMsg = error instanceof Error ? error.message : 'Payment failed';
                        setError(errorMsg);
                        if (onError) {
                            onError(errorMsg);
                        }
                    }
                })();
            };
            // Handle cancellation
            session.oncancel = () => {
                console.log('Apple Pay cancelled by user');
                setProcessingPayment(false);
                if (onCancel) {
                    onCancel();
                }
            };
            // Handle errors
            session.onerror = (event) => {
                console.error('Apple Pay session error:', event);
                setProcessingPayment(false);
            };
            session.begin();
        }
        catch (error) {
            console.error('Failed to start Apple Pay session:', error);
            const errorMsg = 'Failed to start Apple Pay';
            setError(errorMsg);
            if (onError) {
                onError(errorMsg);
            }
        }
    }, [
        checkout,
        validateMerchant,
        tokenizeApplePay,
        processApplePayPayment,
        onSuccess,
        onError,
        onCancel,
    ]);
    return {
        handleApplePayClick,
        processingPayment,
        error,
        clearError: () => setError(null),
    };
}
